import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { CheckCircle, Trash2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageLayout from '../components/PageLayout';

// AnswerBox is outside AdminQAPage so typing never causes parent re-render
const AnswerBox = ({ qaId, onSave }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await onSave(qaId, value.trim());
      setValue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 pt-1">
      <Textarea
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Provide a factual answer — will be added to the knowledge base for future questions..."
        rows={3}
        className="resize-none text-sm"
        data-testid={`answer-input-${qaId}`}
      />
      <Button
        size="sm"
        className="rounded-full bg-primary h-8 text-xs"
        disabled={!value.trim() || saving}
        onClick={handleSave}
        data-testid={`save-answer-${qaId}`}
      >
        <Send className="w-3 h-3 mr-1.5" />
        {saving ? 'Saving...' : 'Save Answer'}
      </Button>
    </div>
  );
};

// QuestionCard is also outside AdminQAPage
const QuestionCard = ({ q, showAnswerBox, onSave, onDelete }) => (
  <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-3" data-testid={`qa-${q.qa_id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-800">{q.question}</p>
        <p className="text-xs text-slate-400 mt-0.5">
          {q.asked_by_user?.name || 'Unknown'} · {new Date(q.asked_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={
          q.admin_answer ? 'bg-green-100 text-green-700 border-0 text-xs' :
          q.forwarded_to_admin ? 'bg-amber-100 text-amber-700 border-0 text-xs' :
          'bg-blue-100 text-blue-700 border-0 text-xs'
        }>
          {q.admin_answer ? 'Answered' : q.forwarded_to_admin ? 'Needs Answer' : 'AI Answered'}
        </Badge>
        <button onClick={() => onDelete(q.qa_id)} className="text-slate-300 hover:text-red-500">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>

    {q.ai_answer && (
      <div className="bg-slate-50 rounded-lg p-3">
        <p className="text-xs font-medium text-slate-400 mb-1">AI Response</p>
        <p className="text-sm text-slate-600">{q.ai_answer}</p>
      </div>
    )}

    {q.admin_answer && (
      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-xs font-medium text-green-600 mb-1">Your Answer (in knowledge base)</p>
        <p className="text-sm text-green-800">{q.admin_answer}</p>
      </div>
    )}

    {showAnswerBox && !q.admin_answer && (
      <AnswerBox qaId={q.qa_id} onSave={onSave} />
    )}
  </div>
);

// ════════════════════════════════════════════════════════════
const AdminQAPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    try {
      const res = await api.get('/qa/all');
      setQuestions(res.data);
    } catch { toast.error('Failed to load questions'); }
    finally { setLoading(false); }
  };

  const handleSaveAnswer = async (qaId, answer) => {
    await api.put(`/qa/${qaId}/answer`, { answer });
    toast.success('Answer saved — added to knowledge base');
    fetchQuestions();
  };

  const handleDelete = async (qaId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/qa/${qaId}`);
      toast.success('Deleted');
      fetchQuestions();
    } catch { toast.error('Failed to delete'); }
  };

  const forwarded = questions.filter(q => q.forwarded_to_admin && !q.admin_answer);
  const answered  = questions.filter(q => q.admin_answer);
  const aiAnswered = questions.filter(q => !q.forwarded_to_admin && q.status === 'answered_by_ai');

  const headerBadge = forwarded.length > 0 ? (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
      {forwarded.length} need{forwarded.length === 1 ? 's' : ''} answer
    </Badge>
  ) : null;

  return (
    <PageLayout title="Q&A Management" headerActions={headerBadge} maxWidth="max-w-4xl">
          <Tabs defaultValue="forwarded">
            <TabsList className="mb-5">
              <TabsTrigger value="forwarded">
                Needs Answer
                {forwarded.length > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white border-0 text-xs">{forwarded.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="answered">Admin Answered ({answered.length})</TabsTrigger>
              <TabsTrigger value="ai">AI Answered ({aiAnswered.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="forwarded">
              {loading ? (
                <div className="text-center py-12 text-slate-400">Loading...</div>
              ) : forwarded.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="text-slate-500">No questions waiting for answers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {forwarded.map(q => (
                    <QuestionCard key={q.qa_id} q={q} showAnswerBox onSave={handleSaveAnswer} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="answered">
              {answered.length === 0 ? (
                <p className="text-center py-12 text-slate-400">No admin-answered questions yet</p>
              ) : (
                <div className="space-y-3">
                  {answered.map(q => (
                    <QuestionCard key={q.qa_id} q={q} showAnswerBox={false} onSave={handleSaveAnswer} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="ai">
              {aiAnswered.length === 0 ? (
                <p className="text-center py-12 text-slate-400">No AI-answered questions yet</p>
              ) : (
                <div className="space-y-3">
                  {aiAnswered.map(q => (
                    <QuestionCard key={q.qa_id} q={q} showAnswerBox onSave={handleSaveAnswer} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
    </PageLayout>
  );
};

export default AdminQAPage;
